import React, { useState } from "react";
import { Button, message, Modal, Space } from "antd";
import { OrderFileDto, OrderVersionDto } from "../../../interfaces/OrderDetail";
import { createOrder } from "../../../axios/orderDetail";
import { QuoteHeader } from "./QuoteHeader";
import { AddressColumns } from "./AddressColumns";
import { PoolCoverSpecs } from "./PoolCoverSpecs";
import { LineItemsTable } from "./LineItemsTable";
import { PricingSummaryTable } from "./PricingSummaryTable";
import { HardwareReplacementModal } from "./HardwareReplacementModal";
import { useOrderDetailsEdit } from "./hooks/useOrderDetailsEdit";
import Flex from "../../../components/Flex";

interface QuoteVersionDetailProps {
  version: OrderVersionDto;
  allowUpdates?: boolean;
  onSaved?: () => void;
  onRequestComment?: (text: string) => void;
}

export const QuoteVersionDetail: React.FC<QuoteVersionDetailProps> = ({
  version,
  allowUpdates,
  onSaved,
  onRequestComment
}) => {
  const showEdit = !!(allowUpdates && onSaved);

  const edit = useOrderDetailsEdit({ version, soNo: version.soNo, onSaved });

  // While editing, prefer the live (debounced) recalc preview's totals over the last-saved
  // version's — but only once a successful preview has actually landed; fall back to
  // version.* before the first preview resolves, or if the last preview failed / signaled an
  // invalid staged hardware swap (previewTotals.success === false is never stored, see
  // useOrderDetailsEdit).
  const usePreviewTotals = edit.editing && edit.previewTotals != null;

  const handleCreateOrder = () => {
    const modal = Modal.confirm({
      title: "Convert To Order",
      content:
        "This will delete all other versions and convert this quote to an order. Are you sure?",
      okText: "Convert To Order",
      okType: "primary",
      cancelText: "Cancel",
      centered: true,
      onOk: async () => {
        modal.update({ cancelButtonProps: { disabled: true }, closable: false });
        try {
          await createOrder(version.soNo, version.versionLabel);
          onSaved?.();
        } catch {
          modal.update({ cancelButtonProps: { disabled: false }, closable: true });
          message.error("Failed to create order. Please try again.");
          throw new Error();
        }
      },
    });
  };

  return (
    <>
      <Flex
        vertical
        align="flex-start"
        justify="space-between"
        gap={8}
        style={{ paddingBottom: 8 }}
      >
        {(showEdit || (!version.isOrder && onSaved)) && (
          <Space size="small" style={{ marginBottom: 8 }}>
            {showEdit && !edit.editing && (
              <Button
                size="small"
                onClick={edit.handleEdit}
                loading={edit.loadingOptions}
              >
                Edit
              </Button>
            )}
            {showEdit && edit.editing && (
              <>
                <Button
                  size="small"
                  type="primary"
                  onClick={edit.handleSave}
                  loading={edit.saving}
                  disabled={!edit.isValid || !edit.hasChanges}
                >
                  Save
                </Button>
                <Button
                  size="small"
                  onClick={edit.handleCancel}
                  disabled={edit.saving}
                >
                  Cancel
                </Button>
              </>
            )}
            {!version.isOrder && !edit.editing && allowUpdates && (
              <Button
                size="small"
                onClick={handleCreateOrder}
                disabled={edit.saving}
              >
                Convert To Order
              </Button>
            )}
          </Space>
        )}
      </Flex>
      <QuoteHeader
        soNo={version.soNo}
        versionLabel={version.versionLabel}
        orderDate={version.orderDate}
        shipDate={version.shipDate}
        poNumber={version.poNumber}
        terms={version.terms}
        depositRequired={usePreviewTotals ? edit.previewTotals!.depositRequired : version.depositRequired}
        depositRequiredLoading={edit.editing && edit.previewLoading}
        editing={edit.editing}
        saving={edit.saving}
        draftPoNumber={edit.draft.poNumber}
        onDraftPoNumberChange={(v) =>
          edit.setDraft((d) => ({ ...d, poNumber: v }))
        }
      />

      <AddressColumns
        version={version}
        editing={edit.editing}
        saving={edit.saving}
        draft={edit.draft}
        onDraftChange={(patch) => edit.setDraft((d) => ({ ...d, ...patch }))}
        usStateOptions={edit.usStateOptions}
      />

      <PoolCoverSpecs
        poolDimensions={version.poolDimensions}
        coverDimensions={version.coverDimensions}
        straps={version.straps}
        color={version.color}
        spacing={version.spacing}
        overlap={version.overlap}
        productLine={version.productLine}
        material={version.material}
        editing={edit.editing}
        options={edit.coverOptions}
        selectedColorId={edit.selectedColorId}
        selectedMaterialId={edit.selectedMaterialId}
        onColorChange={edit.setSelectedColorId}
        onMaterialChange={edit.setSelectedMaterialId}
      />

      <LineItemsTable
        lineItems={version.lineItems}
        editing={showEdit && edit.editing}
        productLine={version.productLine ?? ""}
        onRequestComment={onRequestComment}
        onEditHardware={edit.openHardwareReplacement}
        pendingHardwareChanges={edit.hwPendingChanges}
        previewTotals={edit.previewTotals}
      />

      <PricingSummaryTable
        subtotal={usePreviewTotals ? edit.previewTotals!.subtotal : version.subtotal}
        shipping={usePreviewTotals ? edit.previewTotals!.shipping : version.shipping}
        tax={usePreviewTotals ? edit.previewTotals!.tax : version.tax}
        additionalDiscount={version.additionalDiscount}
        additionalDiscountNote={version.additionalDiscountNote}
        orderAmount={usePreviewTotals ? edit.previewTotals!.orderAmount : version.orderAmount}
        amountPaid={version.amountPaid}
        listSubtotal={usePreviewTotals ? edit.previewTotals!.listSubtotal : version.listSubtotal}
        isOrder={version.isOrder}
        previewLoading={edit.editing && edit.previewLoading}
      />

      <HardwareReplacementModal
        open={!!edit.hwTargetLine}
        currentItem={edit.hwCurrentItem}
        currentQuantity={edit.hwCurrentQuantity}
        candidates={edit.hwCandidates}
        loading={edit.hwLoadingCandidates}
        onCancel={edit.closeHardwareReplacement}
        onConfirm={edit.confirmHardwareReplacement}
      />
    </>
  );
};
